import i18n from "i18next";

export const languageLabels = {
  es: "navbar.spanish",
  en: "navbar.english",
  ca: "navbar.catalan",
  gl: "navbar.galego",
  eu: "navbar.euskara",
};

export function addLanguageTab(
  code,
  baseLang,
  languageTabs,
  translatableFields,
  formData,
  setFormData,
  setLanguageTabs,
  setActiveLang,
) {
  if (!code || languageTabs.includes(code)) return;
  setLanguageTabs((prev) => [...prev, code]);
  setActiveLang(code);

  setFormData((prev) => {
    let next = prev;
    translatableFields.forEach((field) => {
      const baseText = getTextForField(next, field, baseLang, baseLang);
      next = setTextForField(next, field, code, baseText || "");
    });
    return next;
  });
}

export function removeLanguageTab(
  langCode,
  activeLang,
  translatableFields,
  setFormData,
  setLanguageTabs,
  setActiveLang,
) {
  if (!langCode) return;

  setLanguageTabs((prev) => prev.filter((c) => c !== langCode));
  setActiveLang((prev) => (prev === langCode ? "" : prev));

  setFormData((prev) => {
    let next = prev;
    translatableFields.forEach((field) => {
      next = removeLangFromField(next, field, langCode);
    });
    return next;
  });
}

export const getTextForField = (
  formData,
  field,
  activeLang,
  baseLang,
  langOverride,
) => {
  if (!formData || !field) return "";

  const parts = field.split(".");
  let node = formData;
  for (const p of parts) {
    node = node?.[p];
    if (node === undefined) return "";
  }

  const lang = langOverride ?? (activeLang === "" ? baseLang : activeLang);

  if (Array.isArray(node)) {
    const isLangObjArray = node.some(
      (it) =>
        it && typeof it === "object" && ("language" in it || "lang" in it),
    );

    if (isLangObjArray) {
      const getLang = (it) => it?.lang ?? it?.language;
      const getText = (it) => it?.text ?? it?.value ?? "";

      const byActive = node.find((it) => getLang(it) === lang);
      if (byActive) return getText(byActive) || "";

      const byBase = node.find((it) => getLang(it) === baseLang);
      if (byBase) return getText(byBase) || "";

      return getText(node[0]) || "";
    }

    return typeof node[0] === "string" ? node[0] || "" : "";
  }

  if (typeof node === "string") return node;

  if (node && typeof node === "object") {
    return node.text ?? node.value ?? "";
  }

  return "";
};

export const setTextForField = (prevData, field, language, value) => {
  if (!field) return prevData;

  const parts = field.split(".");
  const newRoot = { ...prevData };

  let cursorOld = prevData;
  let cursorNew = newRoot;

  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const oldVal = cursorOld?.[key];

    const cloned = Array.isArray(oldVal)
      ? [...oldVal]
      : oldVal && typeof oldVal === "object"
        ? { ...oldVal }
        : {};

    cursorNew[key] = cloned;
    cursorOld = oldVal || {};
    cursorNew = cursorNew[key];
  }

  const lastKey = parts[parts.length - 1];
  const oldValue = cursorOld?.[lastKey];

  const getLang = (it) => it?.language ?? it?.lang;
  const getText = (it) => it?.value ?? it?.text ?? "";

  const normalized = Array.isArray(oldValue)
    ? oldValue
        .filter((it) => it && typeof it === "object")
        .map((it) => ({ language: getLang(it), value: getText(it) }))
        .filter(
          (it) => typeof it.language === "string" && it.language.trim() !== "",
        )
    : [];

  const idx = normalized.findIndex((e) => e.language === language);
  const newEntry = { language, value: value ?? "" };

  let newArr;
  if (idx >= 0) {
    newArr = [...normalized];
    newArr[idx] = newEntry;
  } else {
    newArr = [...normalized, newEntry];
  }

  cursorNew[lastKey] = newArr;
  return newRoot;
};

export function removeLangFromField(prevData, field, langToRemove) {
  if (!field || !langToRemove) return prevData;

  const parts = field.split(".");
  const newRoot = { ...prevData };

  let cursorOld = prevData;
  let cursorNew = newRoot;

  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    const oldVal = cursorOld?.[key];

    if (oldVal == null || typeof oldVal !== "object") {
      return prevData;
    }

    cursorNew[key] = { ...oldVal };

    cursorOld = oldVal;
    cursorNew = cursorNew[key];
  }

  const lastKey = parts[parts.length - 1];
  const oldArr = cursorOld?.[lastKey];

  if (!Array.isArray(oldArr)) {
    cursorNew[lastKey] = oldArr;
    return newRoot;
  }

  cursorNew[lastKey] = oldArr.filter(
    (item) =>
      !(
        item &&
        (item.language === langToRemove || item.lang === langToRemove)
      ),
  );

  return newRoot;
}

export const getTextByLang = (field, fallbackLang = "es") => {
  if (!field) return "Not specified.";

  const currentLang = i18n.language || fallbackLang;

  const pick = (obj) => obj?.value ?? obj?.text ?? obj?.label ?? obj?.name;

  if (Array.isArray(field)) {
    const exact =
      field.find((i) => (i.language ?? i.lang) === currentLang) ??
      field.find((i) => (i.language ?? i.lang) === fallbackLang) ??
      field[0];

    return pick(exact) ?? "Not specified.";
  }

  if (typeof field === "string") return field;

  if (field && typeof field === "object") {
    return pick(field) ?? "Not specified.";
  }

  return "Not specified.";
};

export const formatArray = (arr, separator = ", ", fallback = "-") => {
  if (Array.isArray(arr) && arr.length > 0) {
    return arr.join(separator);
  }
  return arr || fallback;
};

export const isValidHttpUrl = (value) => {
  if (!value?.trim()) return false;

  try {
    const url = new URL(value.trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export const normalizeLangArray = (arr, fallbackLang) => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return [{ language: fallbackLang, value: "" }];
  }

  const cleaned = arr
    .map((item) => ({
      language: item?.language ? String(item.language) : fallbackLang,
      value: item?.value ? String(item.value) : "",
    }))
    .filter((item) => item.language);

  return cleaned.length > 0
    ? cleaned
    : [{ language: fallbackLang, value: "" }];
};

export const stableSortLangEntries = (arr = []) =>
  [...arr].sort((a, b) =>
    String(a.language).localeCompare(String(b.language)),
  );

export const normalizeTrimmedLangEntries = (arr = []) =>
  stableSortLangEntries(arr).map((item) => ({
    language: String(item.language),
    value: String(item.value).trim(),
  }));

export const parseCommaSeparatedList = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

export const joinTruthyList = (arr, separator = ", ") => {
  if (!Array.isArray(arr)) return "";
  return arr.filter(Boolean).join(separator);
};

export const renderLinks = (links, className = "pdetails plink") => {
  if (!links) return <span>-</span>;

  const linkArray = Array.isArray(links) ? links : [links];

  if (linkArray.length === 0) return <span>-</span>;

  return linkArray.map((link, index) => (
    <a
      key={index}
      href={link}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
    >
      {link}
    </a>
  ));
};

export const getBreadcrumbLabel = (record) => {
  return (
    getTextByLang(record?.name) ||
    getTextByLang(record?.resource_name) ||
    getTextByLang(record?.resource_title) ||
    (Array.isArray(record?.creator) ? record.creator[0] : record?.creator) ||
    "-"
  );
};

const MIME_TYPE_HEADERS = {
  ttl: "text/turtle",
  rdf: "application/rdf+xml",
  csv: "text/csv",
  txt: "text/plain",
  pdf: "application/pdf",
  zip: "application/zip",
  json: "application/json",
  nt: "application/n-triples",
  nq: "application/n-quads",
  xml: "application/xml",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  jsonld: "application/ld+json",
};

const BACKEND_TYPES = [
  { code: "nq", langName: { eng: "n-quads", spa: "n-quads" } },
  { code: "nt", langName: { eng: "n-triples", spa: "n-triples" } },
  { code: "rdf", langName: { eng: "rdf", spa: "rdf" } },
  { code: "ttl", langName: { eng: "turtle", spa: "turtle" } },
  { code: "jsonld", langName: { eng: "json-ld", spa: "json-ld" } },
  { code: "csv", langName: { eng: "csv", spa: "csv" } },
  { code: "xls", langName: { eng: "ms-excel", spa: "ms-excel" } },
  { code: "json", langName: { eng: "json", spa: "json" } },
  { code: "xml", langName: { eng: "xml", spa: "xml" } },
  { code: "zip", langName: { eng: "zip", spa: "zip" } },
];

const MIME_TO_CODE = Object.fromEntries(
  Object.entries(MIME_TYPE_HEADERS).map(([code, mime]) => [mime, code]),
);

export function mimeToKey(value) {
  if (!value) return null;

  let clean = value.toLowerCase().trim();
  clean = clean.split(";")[0].trim();

  const exactCode = BACKEND_TYPES.find((t) => t.code === clean);
  if (exactCode) return exactCode.langName.eng;

  const matchExt = clean.match(/\.([a-z0-9]+)$/);
  if (matchExt) {
    const ext = matchExt[1];
    const found = BACKEND_TYPES.find((t) => t.code === ext);
    if (found) return found.langName.eng;
  }

  if (MIME_TO_CODE[clean]) {
    const code = MIME_TO_CODE[clean];
    const found = BACKEND_TYPES.find((t) => t.code === code);
    if (found) return found.langName.eng;
  }

  for (const [mime, code] of Object.entries(MIME_TO_CODE)) {
    if (mime.includes(clean)) {
      const found = BACKEND_TYPES.find((t) => t.code === code);
      if (found) return found.langName.eng;
    }
  }

  for (const t of BACKEND_TYPES) {
    const eng = t.langName.eng.toLowerCase();
    if (eng === clean || eng.includes(clean) || clean.includes(eng)) {
      return t.langName.eng;
    }
  }

  return null;
}
